'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createTimedFetch, isTransientConnectivityError } from '@/lib/query-resilience';

const latencyDegradedState = { current: false };
function getLatencyDegraded() { return latencyDegradedState.current; }
function subscribeLatency(_listener: () => void): () => void { return () => {}; }
import {
  enqueueAction,
  listPendingActions,
  removeAction,
  markActionFailed,
  countPendingActions,
  type ActionScope,
  type QueuedAction,
} from '@/lib/offline/action-queue';

interface OfflineContextProps {
  /** `navigator.onLine` , the browser thinks there is a network interface. */
  isOnline: boolean;
  /**
   * The browser says we are online but requests are actually failing or timing
   * out. `navigator.onLine` is famously optimistic: a phone on one bar of
   * signal, a captive portal and a dead upstream all report `true`. Everything
   * that used to key off "offline" to fall back to cached data and the action
   * queue should treat `isDegraded` the same way, which is what makes the
   * offline experience cover weak connections and not just severed ones.
   */
  isDegraded: boolean;
  /** True when we cannot reach the server, for either reason above. */
  isUnreachable: boolean;
  wasOffline: boolean;
  pendingCount: number;
  scopePending: (scope: ActionScope) => Promise<number>;
  queueAction: (
    scope: ActionScope,
    type: string,
    payload: Record<string, unknown>,
    opts?: { metadata?: Record<string, unknown>; maxAttempts?: number },
  ) => Promise<string>;
  registerSyncHandler: (
    scope: ActionScope,
    handler: (action: QueuedAction) => Promise<boolean>,
  ) => () => void;
  triggerSync: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextProps | null>(null);

export const useOffline = () => {
  const ctx = useContext(OfflineContext);
  if (!ctx) throw new Error('useOffline must be used within OfflineProvider');
  return ctx;
};

const MAX_BACKOFF_MS = 30_000;
const BASE_BACKOFF_MS = 1_000;

/** Consecutive connectivity failures before we call the connection degraded. */
const DEGRADED_FAILURE_THRESHOLD = 2;
/** Auto-clear the degraded flag if nothing has failed for this long. */
const DEGRADED_RESET_MS = 20_000;

/** First delay before a connection probe fires while we are unreachable. */
const PROBE_BASE_DELAY_MS = 2_000;
/** Cap on how long we wait between probe attempts. */
const PROBE_MAX_DELAY_MS = 30_000;
/** Abort the health probe after this long so a dead connection can't hang it. */
const PROBE_TIMEOUT_MS = 5_000;

export function OfflineProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [isOnline, setIsOnline] = useState(true);
  // Failure-rate degradation (react-query cache subscription) and latency
  // degradation (inline latency window) are tracked separately and OR-ed
  // into the exposed `isDegraded` (R27.2: degraded from latency OR failure rate).
  const [failureDegraded, setFailureDegraded] = useState(false);
  const [latencyDegraded, setLatencyDegraded] = useState(false);
  const isDegraded = failureDegraded || latencyDegraded;
  const [wasOffline, setWasOffline] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const syncingRef = useRef(false);
  const syncHandlersRef = useRef<Map<ActionScope, (action: QueuedAction) => Promise<boolean>>>(new Map());
  const onlineRef = useRef(true);
  const failureStreakRef = useRef(0);
  const probeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const probeDelayRef = useRef(PROBE_BASE_DELAY_MS);

  const refreshCount = useCallback(async () => {
    if (typeof indexedDB === 'undefined') return;
    const count = await countPendingActions();
    setPendingCount(count);
  }, []);

  // Online/offline detection
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setIsOnline(navigator.onLine);
    onlineRef.current = navigator.onLine;

    const handleOffline = () => {
      onlineRef.current = false;
      setIsOnline(false);
    };

    const handleOnline = () => {
      onlineRef.current = true;
      setWasOffline(true);
      setIsOnline(true);
      window.setTimeout(() => setWasOffline(false), 3000);
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  // Degraded-connection detection.
  //
  // Watches every React Query result in the app and decides, from real request
  // outcomes, whether the server is actually reachable. This is the signal that
  // lets a weak connection behave like an offline one , showing cached data and
  // a reconnecting strip , instead of hitting a dead-end error screen.
  useEffect(() => {
    const cache = queryClient.getQueryCache();
    let resetTimer: ReturnType<typeof setTimeout> | null = null;

    const clearDegraded = () => {
      failureStreakRef.current = 0;
      if (resetTimer) {
        clearTimeout(resetTimer);
        resetTimer = null;
      }
      setFailureDegraded(false);
    };

    const unsubscribe = cache.subscribe((event) => {
      if (event.type !== 'updated') return;
      const actionType = event.action?.type;

      // Any request that gets through means the connection is usable again.
      if (actionType === 'success') {
        clearDegraded();
        return;
      }

      // 'failed' is dispatched per retry attempt, 'error' once retries are
      // exhausted. Counting both means a struggling connection is recognised
      // during the retry storm rather than a minute after it started.
      if (actionType !== 'failed' && actionType !== 'error') return;
      if (!isTransientConnectivityError(event.query.state.error)) return;

      failureStreakRef.current += 1;
      if (failureStreakRef.current >= DEGRADED_FAILURE_THRESHOLD) {
        setFailureDegraded(true);
      }

      if (resetTimer) clearTimeout(resetTimer);
      resetTimer = setTimeout(clearDegraded, DEGRADED_RESET_MS);
    });

    return () => {
      unsubscribe();
      if (resetTimer) clearTimeout(resetTimer);
    };
  }, [queryClient]);

  // Latency-based degradation (OFF-05): a rolling window of slow successful
  // requests trips `latencyDegraded`, OR-ed into `isDegraded`.
  useEffect(() => {
    return subscribeLatency(() => setLatencyDegraded(getLatencyDegraded()));
  }, []);

  // Any hard online/offline transition resets the degraded heuristic: going
  // offline hands the display over to the offline banner, and coming back
  // deserves a clean slate rather than the previous outage's failure streak.
  useEffect(() => {
    failureStreakRef.current = 0;
    setFailureDegraded(false);
  }, [isOnline]);

  // Sync engine: when coming back online, drain the queue
  const sync = useCallback(async () => {
    if (syncingRef.current || typeof indexedDB === 'undefined') return;
    syncingRef.current = true;
    try {
      const pending = await listPendingActions();
      for (const action of pending) {
        const handler = syncHandlersRef.current.get(action.scope);
        if (!handler) continue;
        const ok = await handler(action).catch(() => false);
        if (ok) {
          await removeAction(action.id);
        } else {
          await markActionFailed(action.id, 'Sync handler returned false');
          break;
        }
      }
    } finally {
      syncingRef.current = false;
      await refreshCount();
    }
  }, [refreshCount]);

  // Drain queue on reconnect
  useEffect(() => {
    if (isOnline && pendingCount > 0) {
      let backoff = BASE_BACKOFF_MS;
      const attemptSync = async () => {
        if (!onlineRef.current) return;
        await sync();
        const remaining = await countPendingActions();
        if (remaining > 0) {
          backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
          setTimeout(attemptSync, backoff);
        }
      };
      attemptSync();
    }
  }, [isOnline, pendingCount, sync]);

  const queueAction = useCallback(
    async (
      scope: ActionScope,
      type: string,
      payload: Record<string, unknown>,
      opts?: { metadata?: Record<string, unknown>; maxAttempts?: number },
    ) => {
      const id = await enqueueAction({
        id: crypto.randomUUID(),
        scope,
        type,
        payload,
        metadata: opts?.metadata ?? {},
        createdAt: Date.now(),
        maxAttempts: opts?.maxAttempts ?? 3,
      });
      await refreshCount();
      return id;
    },
    [refreshCount],
  );

  const registerSyncHandler = useCallback(
    (scope: ActionScope, handler: (action: QueuedAction) => Promise<boolean>) => {
      syncHandlersRef.current.set(scope, handler);
      return () => {
        syncHandlersRef.current.delete(scope);
      };
    },
    [],
  );

  const triggerSync = useCallback(async () => {
    await sync();
  }, [sync]);

  // Proactive connection probe.
  //
  // `navigator.onLine` is famously optimistic: a dead Wi-Fi upstream, a captive
  // portal, or a router that lost its WAN link all keep reporting "online", and
  // a real drop is only surfaced once the browser happens to notice the
  // interface is back. While we are unreachable we ping the health endpoint on
  // an exponential backoff, so recovery is detected within a few seconds
  // instead of whenever the browser gets around to it. Success clears the
  // degraded flag, refetches stale queries, resumes paused mutations, and
  // drains the action queue, which is what powers the "retrying…" pill.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const unreachable = !onlineRef.current || isDegraded;
    if (!unreachable) {
      probeDelayRef.current = PROBE_BASE_DELAY_MS;
      if (probeTimerRef.current) {
        clearTimeout(probeTimerRef.current);
        probeTimerRef.current = null;
      }
      return;
    }

    let cancelled = false;
    const probe = async () => {
      try {
        await createTimedFetch(PROBE_TIMEOUT_MS)('/api/health', {
          method: 'GET',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
      } catch {
        if (cancelled) return;
        probeDelayRef.current = Math.min(
          probeDelayRef.current * 2,
          PROBE_MAX_DELAY_MS,
        );
        probeTimerRef.current = setTimeout(probe, probeDelayRef.current);
        return;
      }

      if (cancelled) return;

      probeDelayRef.current = PROBE_BASE_DELAY_MS;
      failureStreakRef.current = 0;
      setFailureDegraded(false);
      if (!onlineRef.current) {
        setWasOffline(true);
        window.setTimeout(() => setWasOffline(false), 3000);
      }
      queryClient.resumePausedMutations();
      queryClient.invalidateQueries();
      void sync();
    };

    probeTimerRef.current = setTimeout(probe, probeDelayRef.current);

    return () => {
      cancelled = true;
      if (probeTimerRef.current) {
        clearTimeout(probeTimerRef.current);
        probeTimerRef.current = null;
      }
    };
  }, [isOnline, isDegraded, queryClient, sync]);

  const scopePending = useCallback(async (scope: ActionScope) => {
    const pending = await listPendingActions(scope);
    return pending.length;
  }, []);

  const value = useMemo<OfflineContextProps>(
    () => ({
      isOnline,
      isDegraded,
      isUnreachable: !isOnline || isDegraded,
      wasOffline,
      pendingCount,
      scopePending,
      queueAction,
      registerSyncHandler,
      triggerSync,
    }),
    [isOnline, isDegraded, wasOffline, pendingCount, scopePending, queueAction, registerSyncHandler, triggerSync],
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}
